import {
  Fragment,
  StrictMode,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { createRoot } from "react-dom/client";
import { Brand } from "./components/brand.tsx";
import { GuestEntryScreen } from "./components/guest-entry-screen.tsx";
import { GuestEntry } from "./guest-entry.ts";
import { parseLogoutRedirect } from "./logout";
import { selectOrganizationSession } from "./organization-selection.ts";
import {
  Add01Icon,
  ArrowDown01Icon,
  Comment01Icon,
  ComputerTerminal01Icon,
  Copy01Icon,
  CreditCardIcon,
  Delete02Icon,
  ExternalLinkIcon,
  Key01Icon,
  LinkSquare01Icon,
  Logout01Icon,
  Moon02Icon,
  PauseIcon,
  PackageAddIcon,
  PlayIcon,
  Sun03Icon,
  Tick01Icon,
  UserGroup03Icon,
  WebDesign01Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";

import {
  AgentPromptShortcut,
  AgentSetupDialog,
  type AgentSetupDialogKind,
} from "@/components/agent-setup";
import {
  ConsoleSection,
  PageHeader,
  SecretPanel,
  StatePanel,
  StatusBadge,
} from "@/components/product";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  Field as FieldRoot,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  NativeSelect,
  NativeSelectOption,
} from "@/components/ui/native-select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  ApiError,
  type ApiKey,
  type BillingSummary,
  type GuestLink,
  type Me,
  type Membership,
  type MembershipManagement,
  type Note,
  type Reply,
  type Site,
  type TeamMember,
} from "./api.ts";
import "./index.css";
import {
  canAdministerMember,
  canAdministerWorkspace,
  canManageMembers,
  canModifyAuthoredContent,
  canReplyToNote,
} from "./permissions.ts";
import { launchSiteReview } from "./site-launch.ts";
import {
  usePendingActions,
  useRetryOperationIds,
  type PendingActions,
} from "./pending-actions.ts";
import { OrganizationResponseGuard } from "./organization-response-guard.ts";
import { useScopedApi } from "./use-scoped-api.ts";
import { useTheme } from "./use-theme.ts";
import { ConnectedAgents } from "./components/connected-agents.tsx";
import { NoteImage } from "./components/note-image.tsx";
import {
  createBrowserCredentialRecoveryStore,
  type CredentialRecovery,
} from "./credential-recovery.ts";

type Section = "sites" | "notes" | "access" | "team" | "billing";

const documentationUrl = "https://appnotes.dev/docs";

type RevealedCredential = {
  readonly label: string;
  readonly recovery: CredentialRecovery;
  readonly value: string;
};

type Inventory<T> =
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly items: readonly T[] }
  | { readonly status: "error"; readonly message: string };

function App() {
  const api = useScopedApi();
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const { dark, toggle } = useTheme();
  const meRequest = useRef<AbortController | null>(null);

  async function loadMe(): Promise<void> {
    meRequest.current?.abort();
    const controller = new AbortController();
    meRequest.current = controller;
    setError(null);
    setMe(undefined);
    try {
      setMe(await api<Me>("/v1/me", { signal: controller.signal }));
      setError(null);
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === "request_cancelled")
        return;
      if (cause instanceof ApiError && cause.status === 401) setMe(null);
      else setError(errorMessage(cause));
    }
  }

  useEffect(() => void loadMe(), []);
  if (error !== null) {
    return <FatalScreen message={error} retry={() => void loadMe()} />;
  }
  if (me === undefined) return <LoadingScreen />;
  if (me === null) return <SignInScreen />;
  if (me.selected_organization_id === null) {
    return (
      <OrganizationGate me={me} onChange={setMe} onSessionError={setError} />
    );
  }
  const membership = me.memberships.find(
    (candidate) => candidate.organization_id === me.selected_organization_id,
  );
  if (membership === undefined) {
    return (
      <FatalScreen
        message="Your selected team is no longer available."
        retry={() => void loadMe()}
      />
    );
  }
  return (
    <Workspace
      dark={dark}
      me={me}
      membership={membership}
      onMeChange={setMe}
      onSessionError={setError}
      onThemeChange={toggle}
    />
  );
}

function Workspace({
  dark,
  me,
  membership,
  onMeChange,
  onSessionError,
  onThemeChange,
}: {
  readonly dark: boolean;
  readonly me: Me;
  readonly membership: Membership;
  readonly onMeChange: (me: Me | null) => void;
  readonly onSessionError: (message: string) => void;
  readonly onThemeChange: () => void;
}) {
  const api = useScopedApi();
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [section, setSection] = useState<Section>("sites");
  const [accessSiteId, setAccessSiteId] = useState<string | null>(null);
  const [agentSetup, setAgentSetup] = useState<AgentSetupDialogKind | null>(
    null,
  );
  const [revision, setRevision] = useState(0);
  const pending = usePendingActions();
  const refresh = () => setRevision((value) => value + 1);

  function openAccess(siteId: string | null): void {
    setAccessSiteId(siteId);
    setSection("access");
  }

  useEffect(() => setAccessSiteId(null), [membership.organization_id]);

  async function selectOrganization(organizationId: string): Promise<void> {
    await pending.run("session:navigation", async () => {
      setSessionError(null);
      const result = await selectOrganizationSession(
        api,
        organizationId,
        me.csrf_token,
      );
      switch (result.status) {
        case "confirmed":
          onMeChange(result.me);
          setSessionError(result.notice);
          break;
        case "signed_out":
          onMeChange(null);
          break;
        case "unavailable":
          onSessionError(result.message);
          break;
        case "cancelled":
          break;
      }
    });
  }

  async function logout(): Promise<void> {
    await pending.run("session:navigation", async () => {
      setSessionError(null);
      try {
        const response = await api<unknown>("/auth/logout", {
          method: "POST",
          csrf: me.csrf_token,
        });
        const target = parseLogoutRedirect(response, window.location.origin);
        if (target === null) {
          setSessionError(
            "AppNotes returned an invalid sign-out response. Reload to check your session before trying again.",
          );
          return;
        }
        window.location.assign(target.href);
      } catch (cause) {
        setSessionError(`Could not confirm sign out. ${errorMessage(cause)}`);
      }
    });
  }

  return (
    <div className="workspace-shell">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <aside className="workspace-sidebar">
        <div className="workspace-sidebar-brand">
          <Brand />
        </div>
        <div className="workspace-mobile-controls">
          <span className="workspace-mobile-team">
            <NativeSelect
              aria-label="Current team"
              disabled={pending.isPending("session:navigation")}
              value={membership.organization_id}
              onChange={(event) => void selectOrganization(event.target.value)}
            >
              {me.memberships.map((item) => (
                <NativeSelectOption
                  key={item.organization_id}
                  value={item.organization_id}
                >
                  {item.organization_name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  aria-label="Open account menu"
                  size="icon"
                  type="button"
                  variant="ghost"
                />
              }
            >
              <AvatarForUser
                name={me.user.display_name}
                url={me.user.avatar_url}
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuGroup>
                <DropdownMenuLabel>
                  <span className="workspace-mobile-account-name">
                    {me.user.display_name}
                  </span>
                  <span className="workspace-mobile-account-role">
                    {membership.role}
                  </span>
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={onThemeChange}>
                  <HugeiconsIcon
                    icon={dark ? Sun03Icon : Moon02Icon}
                    strokeWidth={1.8}
                  />
                  Use {dark ? "light" : "dark"} theme
                </DropdownMenuItem>
                <DropdownMenuItem
                  render={
                    <a
                      href={documentationUrl}
                      rel="noreferrer"
                      target="_blank"
                    />
                  }
                >
                  <HugeiconsIcon icon={ExternalLinkIcon} strokeWidth={1.8} />
                  Documentation
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setAgentSetup("install")}>
                  <HugeiconsIcon icon={PackageAddIcon} strokeWidth={1.8} />
                  Install skill
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setAgentSetup("instructions")}>
                  <HugeiconsIcon
                    icon={ComputerTerminal01Icon}
                    strokeWidth={1.8}
                  />
                  Agent instructions
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem
                  disabled={pending.isPending("session:navigation")}
                  onClick={() => void logout()}
                >
                  <HugeiconsIcon icon={Logout01Icon} strokeWidth={1.8} />
                  {pending.isPending("session:navigation")
                    ? "Please wait…"
                    : "Sign out"}
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="organization-switcher">
          <span className="organization-monogram" aria-hidden="true">
            {initials(membership.organization_name)}
          </span>
          <span className="organization-select">
            <NativeSelect
              aria-label="Current team"
              disabled={pending.isPending("session:navigation")}
              value={membership.organization_id}
              onChange={(event) => void selectOrganization(event.target.value)}
            >
              {me.memberships.map((item) => (
                <NativeSelectOption
                  key={item.organization_id}
                  value={item.organization_id}
                >
                  {item.organization_name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            <small>Team workspace</small>
          </span>
        </div>
        <nav aria-label="AppNotes sections" className="workspace-nav">
          <div className="workspace-nav-group">
            <p className="workspace-nav-label">App Notes</p>
            <div className="workspace-nav-list">
              <NavButton
                active={section === "sites"}
                icon={WebDesign01Icon}
                onClick={() => setSection("sites")}
              >
                Sites
              </NavButton>
              <NavButton
                active={section === "notes"}
                icon={Comment01Icon}
                onClick={() => setSection("notes")}
              >
                Notes
              </NavButton>
            </div>
          </div>
          <div className="workspace-nav-group">
            <p className="workspace-nav-label">Manage</p>
            <div className="workspace-nav-list">
              <NavButton
                active={section === "access"}
                icon={Key01Icon}
                onClick={() => openAccess(null)}
              >
                Access
              </NavButton>
              <NavButton
                active={section === "team"}
                icon={UserGroup03Icon}
                onClick={() => setSection("team")}
              >
                Team
              </NavButton>
              {me.billing_available ? (
                <NavButton
                  active={section === "billing"}
                  icon={CreditCardIcon}
                  onClick={() => setSection("billing")}
                >
                  Billing
                </NavButton>
              ) : null}
            </div>
          </div>
        </nav>
        <div className="workspace-agent-tools">
          <a
            className={buttonVariants({ variant: "ghost" })}
            href={documentationUrl}
            rel="noreferrer"
            target="_blank"
          >
            <HugeiconsIcon
              data-icon="inline-start"
              icon={ExternalLinkIcon}
              strokeWidth={1.8}
            />
            Documentation
          </a>
          <Button
            onClick={() => setAgentSetup("install")}
            type="button"
            variant="ghost"
          >
            <HugeiconsIcon
              data-icon="inline-start"
              icon={PackageAddIcon}
              strokeWidth={1.8}
            />
            Install skill
          </Button>
          <Button
            onClick={() => setAgentSetup("instructions")}
            type="button"
            variant="ghost"
          >
            <HugeiconsIcon
              data-icon="inline-start"
              icon={ComputerTerminal01Icon}
              strokeWidth={1.8}
            />
            Agent instructions
          </Button>
        </div>
        <div className="workspace-account">
          <div className="workspace-account-identity">
            <AvatarForUser
              name={me.user.display_name}
              url={me.user.avatar_url}
            />
            <div className="workspace-account-copy">
              <strong>{me.user.display_name}</strong>
              <span>{membership.role}</span>
            </div>
          </div>
          <div className="workspace-account-actions">
            <Button
              aria-label={dark ? "Use light theme" : "Use dark theme"}
              onClick={onThemeChange}
              size="xs"
              type="button"
              variant="ghost"
            >
              <HugeiconsIcon
                data-icon="inline-start"
                icon={dark ? Sun03Icon : Moon02Icon}
                strokeWidth={1.8}
              />
              <span className="theme-label">{dark ? "Light" : "Dark"}</span>
            </Button>
            <Button
              aria-label="Sign out"
              disabled={pending.isPending("session:navigation")}
              onClick={() => void logout()}
              size="xs"
              type="button"
              variant="ghost"
            >
              <HugeiconsIcon
                data-icon="inline-start"
                icon={Logout01Icon}
                strokeWidth={1.8}
              />
              <span className="signout-label">Sign out</span>
            </Button>
          </div>
        </div>
      </aside>
      <AgentSetupDialog
        apiOrigin={window.location.origin}
        mcpAvailable={me.mcp_available === true}
        kind={agentSetup}
        membership={membership}
        onOpenChange={(open) => {
          if (!open) setAgentSetup(null);
        }}
      />
      <main className="workspace-content" id="main-content" tabIndex={-1}>
        {sessionError !== null && <Notice>{sessionError}</Notice>}
        {pending.isPending("session:navigation") && (
          <Notice>Confirming your session…</Notice>
        )}
        <div inert={pending.isPending("session:navigation")}>
          <Fragment key={membership.organization_id}>
            {section === "sites" ? (
              <SitesPage
                me={me}
                membership={membership}
                revision={revision}
                onChange={refresh}
                onOpenAccess={(siteId) => openAccess(siteId)}
              />
            ) : section === "notes" ? (
              <NotesPage
                me={me}
                membership={membership}
                revision={revision}
                onChange={refresh}
              />
            ) : section === "access" ? (
              <AccessPage
                focusedSiteId={accessSiteId}
                me={me}
                membership={membership}
                revision={revision}
                onChange={refresh}
              />
            ) : section === "team" ? (
              <TeamPage me={me} membership={membership} onChange={refresh} />
            ) : (
              <BillingPage me={me} membership={membership} />
            )}
          </Fragment>
        </div>
      </main>
    </div>
  );
}

function SitesPage({
  me,
  membership,
  revision,
  onChange,
  onOpenAccess,
}: PageProps & { readonly onOpenAccess: (siteId: string) => void }) {
  const api = useScopedApi();
  const [sites, setSites] = useState<readonly Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [createdSite, setCreatedSite] = useState<Site | null>(null);
  const [setupCopied, setSetupCopied] = useState(false);
  const [pendingDeletion, setPendingDeletion] = useState<Site | null>(null);
  const [launchingSiteId, setLaunchingSiteId] = useState<string | null>(null);
  const pending = usePendingActions();
  const administrator = canAdministerWorkspace(membership.role);

  useEffect(() => {
    let current = true;
    const controller = new AbortController();
    setLoading(true);
    api<{ sites: Site[] }>(
      `/v1/orgs/${membership.organization_id}/appnotes/sites?limit=100`,
      { signal: controller.signal },
    )
      .then((value) => current && setSites(value.sites))
      .catch((cause) => current && setMessage(errorMessage(cause)))
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
      controller.abort();
    };
  }, [membership.organization_id, revision]);

  async function createSite(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    await pending.run("site:create", async () => {
      setCreating(true);
      setSetupError(null);
      try {
        const primaryUrl = String(form.get("primary_url") ?? "");
        const created = await api<Site>(
          `/v1/orgs/${membership.organization_id}/appnotes/sites`,
          {
            method: "POST",
            csrf: me.csrf_token,
            body: {
              name: form.get("name"),
              origin: new URL(primaryUrl).origin,
              primary_url: primaryUrl,
            },
          },
        );
        formElement.reset();
        setCreatedSite(created);
        onChange();
      } catch (cause) {
        if (
          cause instanceof ApiError &&
          cause.code === "appnotes_site_origin_conflict"
        ) {
          const existing = await api<{ sites: Site[] }>(
            `/v1/orgs/${membership.organization_id}/appnotes/sites?limit=100`,
          ).catch(() => null);
          const primaryUrl = String(form.get("primary_url") ?? "");
          const site = existing?.sites.find(
            (candidate) => candidate.origin === new URL(primaryUrl).origin,
          );
          if (site !== undefined) {
            setCreatedSite(site);
            setSetupError(null);
            onChange();
            return;
          }
        }
        setSetupError(errorMessage(cause));
      } finally {
        setCreating(false);
      }
    });
  }

  function changeSetupOpen(open: boolean): void {
    setSetupOpen(open);
    if (!open) {
      setCreatedSite(null);
      setSetupError(null);
      setSetupCopied(false);
    }
  }

  async function copySetupInstructions(site: Site): Promise<void> {
    try {
      await navigator.clipboard.writeText(sdkSetupInstructions(site));
      setSetupCopied(true);
      setSetupError(null);
    } catch {
      setSetupError("The SDK instructions could not be copied.");
    }
  }

  async function updateSite(
    site: Site,
    body: { readonly enabled: boolean },
  ): Promise<void> {
    await pending.run(`site:update:${site.id}`, async () => {
      try {
        await api(`/v1/appnotes/sites/${site.id}`, {
          method: "PATCH",
          csrf: me.csrf_token,
          body,
        });
        onChange();
      } catch (cause) {
        setMessage(errorMessage(cause));
      }
    });
  }

  async function deleteSite(site: Site): Promise<void> {
    await pending.run(`site:delete:${site.id}`, async () => {
      try {
        await api(`/v1/appnotes/sites/${site.id}`, {
          method: "DELETE",
          csrf: me.csrf_token,
        });
        setPendingDeletion(null);
        onChange();
      } catch (cause) {
        setMessage(errorMessage(cause));
      }
    });
  }

  async function reviewSite(site: Site): Promise<void> {
    await pending.run(`site:review:${site.id}`, async () => {
      setMessage(null);
      setLaunchingSiteId(site.id);
      const result = await launchSiteReview({
        mint: () =>
          api<{ readonly launch_url: string }>(
            `/v1/appnotes/sites/${site.id}/token`,
            {
              method: "POST",
              csrf: me.csrf_token,
              body: { url: site.primary_url },
            },
          ),
        navigate: (url) => window.location.assign(url),
      });
      if (!result.ok) setMessage(errorMessage(result.cause));
      setLaunchingSiteId(null);
    });
  }

  const activeSites = sites.filter((site) => site.enabled);
  const archivedSites = sites.filter((site) => !site.enabled);

  return (
    <Page
      actions={
        <Dialog
          open={administrator && setupOpen}
          onOpenChange={changeSetupOpen}
        >
          {administrator && (
            <DialogTrigger render={<Button type="button" />}>
              <HugeiconsIcon
                data-icon="inline-start"
                icon={Add01Icon}
                strokeWidth={1.8}
              />
              Add site
            </DialogTrigger>
          )}
          <DialogContent className="site-setup-dialog sm:max-w-2xl">
            <ol aria-label="Site setup progress" className="site-setup-steps">
              <li data-current={createdSite === null}>
                <span>1</span>
                Register site
              </li>
              <li data-current={createdSite !== null}>
                <span>2</span>
                Install SDK
              </li>
            </ol>
            {createdSite === null ? (
              <>
                <DialogHeader>
                  <DialogTitle>Connect a site</DialogTitle>
                  <DialogDescription>
                    Register the web app, then install its AppNotes SDK.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={(event) => void createSite(event)}>
                  <FieldGroup className="gap-5">
                    <Field
                      label="Site name"
                      name="name"
                      placeholder="Customer dashboard"
                      required
                    />
                    <FieldRoot>
                      <FieldLabel htmlFor="field-primary_url">
                        Web app URL
                      </FieldLabel>
                      <Input
                        id="field-primary_url"
                        name="primary_url"
                        type="url"
                        placeholder="https://app.example.com/dashboard"
                        required
                      />
                      <p className="site-setup-field-help">
                        Use the page reviewers should open. Its exact origin is
                        registered automatically.
                      </p>
                    </FieldRoot>
                    {setupError === null ? null : <Notice>{setupError}</Notice>}
                    <DialogFooter>
                      <Button disabled={creating} type="submit">
                        {creating ? "Registering…" : "Continue to SDK"}
                      </Button>
                    </DialogFooter>
                  </FieldGroup>
                </form>
                <AgentPromptShortcut
                  apiOrigin={window.location.origin}
                  mcpAvailable={me.mcp_available === true}
                  membership={membership}
                />
              </>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle>Install the SDK</DialogTitle>
                  <DialogDescription>
                    {createdSite.name} is registered for {createdSite.origin}.
                    Add this configuration to that web app.
                  </DialogDescription>
                </DialogHeader>
                <div className="site-setup-code">
                  <div>
                    <span>Install package</span>
                    <code>npm install @plannotator/appnotes</code>
                  </div>
                  <div>
                    <span>Configure AppNotes</span>
                    <pre>
                      <code>{sdkConfiguration(createdSite)}</code>
                    </pre>
                  </div>
                </div>
                <p className="site-setup-note">
                  Connected mode activates when a reviewer opens this site from
                  AppNotes. Normal visitors will not see the review tools.
                </p>
                {setupError === null ? null : <Notice>{setupError}</Notice>}
                <DialogFooter>
                  <Button
                    onClick={() => void copySetupInstructions(createdSite)}
                    type="button"
                    variant="outline"
                  >
                    <HugeiconsIcon
                      data-icon="inline-start"
                      icon={setupCopied ? Tick01Icon : Copy01Icon}
                      strokeWidth={1.8}
                    />
                    {setupCopied ? "Copied" : "Copy instructions"}
                  </Button>
                  <Button onClick={() => changeSetupOpen(false)} type="button">
                    Done
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      }
      title="Sites"
    >
      {message !== null && <Notice>{message}</Notice>}
      <ConsoleSection
        actions={<StatusBadge>{activeSites.length}</StatusBadge>}
        title="Registered sites"
        titleId="registered-sites-title"
      >
        <div aria-live="polite" aria-busy={loading}>
          {loading ? (
            <LoadingRows />
          ) : sites.length === 0 ? (
            <EmptyState
              title="No sites yet"
              detail={
                administrator
                  ? "Register the first live origin to start collecting notes."
                  : "A team administrator can register the first live origin."
              }
            />
          ) : activeSites.length === 0 ? (
            <EmptyState
              title="No active sites"
              detail={
                administrator
                  ? "Add a site or restore one from the archive."
                  : "A team administrator can restore a site from the archive."
              }
            />
          ) : (
            <div className="site-list">
              {activeSites.map((site) => (
                <SiteRow
                  key={site.id}
                  administrator={administrator}
                  launching={launchingSiteId === site.id}
                  pending={pending.isPending(`site:update:${site.id}`)}
                  onDelete={setPendingDeletion}
                  onOpenAccess={onOpenAccess}
                  onReview={reviewSite}
                  onUpdate={updateSite}
                  site={site}
                />
              ))}
            </div>
          )}
        </div>
      </ConsoleSection>
      {loading || archivedSites.length === 0 ? null : (
        <details className="archived-sites-panel">
          <summary>
            <h2>Archived sites</h2>
            <span className="archived-sites-summary-meta">
              <StatusBadge>{archivedSites.length}</StatusBadge>
              <HugeiconsIcon
                className="archived-sites-chevron"
                icon={ArrowDown01Icon}
                strokeWidth={1.8}
              />
            </span>
          </summary>
          <div className="archived-sites-body">
            <div className="site-list">
              {archivedSites.map((site) => (
                <SiteRow
                  key={site.id}
                  administrator={administrator}
                  launching={launchingSiteId === site.id}
                  pending={pending.isPending(`site:update:${site.id}`)}
                  onDelete={setPendingDeletion}
                  onOpenAccess={onOpenAccess}
                  onReview={reviewSite}
                  onUpdate={updateSite}
                  site={site}
                />
              ))}
            </div>
          </div>
        </details>
      )}
      {administrator && pendingDeletion !== null && (
        <AlertDialog
          open
          onOpenChange={(open) => {
            if (!open) setPendingDeletion(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Delete {pendingDeletion.name}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This permanently deletes {pendingDeletion.name} and all of its
                feedback.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={pending.isPending(
                  `site:delete:${pendingDeletion.id}`,
                )}
                onClick={() => void deleteSite(pendingDeletion)}
                type="button"
                variant="destructive"
              >
                {pending.isPending(`site:delete:${pendingDeletion.id}`)
                  ? "Deleting…"
                  : "Delete site"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </Page>
  );
}

function SiteRow({
  administrator,
  launching,
  onDelete,
  onOpenAccess,
  onReview,
  onUpdate,
  pending,
  site,
}: {
  readonly administrator: boolean;
  readonly launching: boolean;
  readonly onDelete: (site: Site) => void;
  readonly onOpenAccess: (siteId: string) => void;
  readonly onReview: (site: Site) => Promise<void>;
  readonly onUpdate: (
    site: Site,
    body: { readonly enabled: boolean },
  ) => Promise<void>;
  readonly pending: boolean;
  readonly site: Site;
}) {
  return (
    <article className="site-row">
      <div className="site-summary">
        <div className="site-heading">
          <span
            aria-hidden="true"
            className={site.enabled ? "status-dot" : "status-dot paused"}
          />
          <div>
            <h3>{site.name}</h3>
            <a href={site.primary_url} target="_blank" rel="noreferrer">
              {site.origin}
            </a>
          </div>
        </div>
        <dl className="site-metrics">
          <div>
            <dt>Open</dt>
            <dd>{site.open_note_count}</dd>
          </div>
          <div>
            <dt>Total</dt>
            <dd>{site.note_count}</dd>
          </div>
          <div>
            <dt>Activity</dt>
            <dd>{relativeDate(site.last_activity_at)}</dd>
          </div>
        </dl>
      </div>
      <div className="card-actions">
        <Button
          aria-label={`Open ${site.name} in AppNotes`}
          disabled={!site.enabled || launching}
          onClick={() => void onReview(site)}
          size="xs"
          type="button"
          variant="link"
        >
          {launching ? "Opening…" : "Review site"}
          <HugeiconsIcon
            data-icon="inline-end"
            icon={ExternalLinkIcon}
            strokeWidth={1.8}
          />
        </Button>
        {administrator ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              disabled={pending}
              render={<Button size="xs" type="button" variant="outline" />}
            >
              {pending ? "Updating…" : "Manage"}
              <HugeiconsIcon
                data-icon="inline-end"
                icon={ArrowDown01Icon}
                strokeWidth={1.8}
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={() => onOpenAccess(site.id)}>
                  <HugeiconsIcon icon={LinkSquare01Icon} strokeWidth={1.8} />
                  Manage guest access
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={pending}
                  onClick={() =>
                    void onUpdate(site, { enabled: !site.enabled })
                  }
                >
                  <HugeiconsIcon
                    icon={site.enabled ? PauseIcon : PlayIcon}
                    strokeWidth={1.8}
                  />
                  {site.enabled ? "Archive site" : "Restore site"}
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem
                  onClick={() => onDelete(site)}
                  variant="destructive"
                >
                  <HugeiconsIcon icon={Delete02Icon} strokeWidth={1.8} />
                  Delete site
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button
            onClick={() => onOpenAccess(site.id)}
            size="xs"
            type="button"
            variant="outline"
          >
            Guest access
          </Button>
        )}
      </div>
    </article>
  );
}

function NotesPage({ me, membership, revision, onChange }: PageProps) {
  const api = useScopedApi();
  const [notes, setNotes] = useState<readonly Note[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const noteApi = useScopedApi(selectedId ?? "no-note");
  const [filter, setFilter] = useState<"all" | "open" | "resolved">("open");
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const pending = usePendingActions();
  const retryOperations = useRetryOperationIds();
  const responseGuardRef = useRef<OrganizationResponseGuard | null>(null);
  responseGuardRef.current ??= new OrganizationResponseGuard(
    membership.organization_id,
  );
  const responseGuard = responseGuardRef.current;

  async function loadNotes(): Promise<void> {
    const request = responseGuard.begin();
    setLoading(true);
    const query = filter === "all" ? "" : `&state=${filter}`;
    try {
      const value = await api<{ notes: Note[] }>(
        `/v1/orgs/${membership.organization_id}/appnotes/notes?limit=100${query}`,
        { signal: request.signal },
      );
      if (!responseGuard.owns(request)) return;
      setNotes(value.notes);
      setSelectedId((current) =>
        value.notes.some((note) => note.id === current)
          ? current
          : (value.notes[0]?.id ?? null),
      );
      setMessage(null);
    } catch (cause) {
      if (!responseGuard.owns(request)) return;
      setMessage(errorMessage(cause));
    } finally {
      if (responseGuard.owns(request)) setLoading(false);
    }
  }

  useEffect(() => {
    responseGuard.selectOrganization(membership.organization_id);
    setNotes([]);
    setSelectedId(null);
    setMessage(null);
    void loadNotes();
    return () => responseGuard.invalidate();
  }, [membership.organization_id, filter, revision]);
  const selected = notes.find((note) => note.id === selectedId) ?? null;
  useEffect(() => setMessage(null), [selectedId]);
  const canModifySelected =
    selected !== null &&
    canModifyAuthoredContent({
      role: membership.role,
      userId: me.user.id,
      authorId: selected.author,
    });

  async function updateNote(body: unknown, action: string): Promise<void> {
    if (selected === null) return;
    await pending.run(action, async () => {
      try {
        await noteApi(
          `/v1/appnotes/sites/${selected.site_id}/notes/${selected.id}`,
          {
            method: "PATCH",
            csrf: me.csrf_token,
            body,
          },
        );
        await loadNotes();
        onChange();
      } catch (cause) {
        if (requestCanceled(cause)) return;
        setMessage(errorMessage(cause));
      }
    });
  }

  async function addReply(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (selected === null) return;
    const form = event.currentTarget;
    const body = String(new FormData(form).get("body") ?? "");
    const action = `reply:create:${selected.id}`;
    const retryAction = `${action}:${body}`;
    await pending.run(action, async () => {
      try {
        await noteApi(
          `/v1/appnotes/sites/${selected.site_id}/notes/${selected.id}/replies`,
          {
            method: "POST",
            csrf: me.csrf_token,
            idempotencyKey: retryOperations.get(retryAction),
            body: { body },
          },
        );
        retryOperations.complete(retryAction);
        form.reset();
        await loadNotes();
      } catch (cause) {
        if (requestCanceled(cause)) return;
        setMessage(errorMessage(cause));
      }
    });
  }

  async function editBody(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const body = String(new FormData(event.currentTarget).get("body") ?? "");
    if (selected !== null) {
      await updateNote({ body }, `note:edit:${selected.id}`);
    }
  }

  async function deleteNote(): Promise<void> {
    if (selected === null) return;
    await pending.run(`note:delete:${selected.id}`, async () => {
      try {
        await noteApi(
          `/v1/appnotes/sites/${selected.site_id}/notes/${selected.id}`,
          {
            method: "DELETE",
            csrf: me.csrf_token,
          },
        );
        await loadNotes();
        onChange();
      } catch (cause) {
        if (requestCanceled(cause)) return;
        setMessage(errorMessage(cause));
      }
    });
  }

  async function deleteReply(replyId: string): Promise<void> {
    if (selected === null) return;
    await pending.run(`reply:delete:${replyId}`, async () => {
      try {
        await noteApi(
          `/v1/appnotes/sites/${selected.site_id}/notes/${selected.id}/replies/${replyId}`,
          { method: "DELETE", csrf: me.csrf_token },
        );
        await loadNotes();
      } catch (cause) {
        if (requestCanceled(cause)) return;
        setMessage(errorMessage(cause));
      }
    });
  }

  return (
    <Page title="Notes">
      {message !== null && <Notice>{message}</Notice>}
      <Tabs
        onValueChange={(value) => {
          if (value === "open" || value === "resolved" || value === "all") {
            setFilter(value);
          }
        }}
        value={filter}
      >
        <TabsList className="max-w-full overflow-x-auto" variant="line">
          {(["open", "resolved", "all"] as const).map((value) => (
            <TabsTrigger key={value} value={value}>
              {capitalize(value)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {loading && notes.length === 0 ? (
        <LoadingRows />
      ) : notes.length === 0 && message !== null ? (
        <Button
          type="button"
          variant="outline"
          onClick={() => void loadNotes()}
        >
          Retry loading notes
        </Button>
      ) : notes.length === 0 ? (
        <EmptyState
          className="notes-empty-state min-h-56 border border-solid bg-card"
          title="No matching notes"
          detail="Annotations made on a registered site will appear here."
        />
      ) : (
        <div className="notes-layout">
          <div className="note-list" role="list" aria-label="Notes">
            {notes.map((note) => (
              <button
                className="note-row"
                data-active={note.id === selectedId}
                key={note.id}
                onClick={() => setSelectedId(note.id)}
                type="button"
              >
                <span>
                  #{note.number} · {note.site_name ?? "Site"}
                </span>
                <strong>{note.body}</strong>
                <small>{relativeDate(note.updated_at)}</small>
              </button>
            ))}
          </div>
          {selected !== null && (
            <article className="note-detail">
              <header>
                <div>
                  <p className="eyebrow">
                    {selected.site_name} · #{selected.number}
                  </p>
                  <h2>{selected.body}</h2>
                </div>
                <Button
                  disabled={pending.isPending(`note:state:${selected.id}`)}
                  size="sm"
                  type="button"
                  variant="outline"
                  onClick={() =>
                    void updateNote(
                      {
                        state: selected.state === "open" ? "resolved" : "open",
                      },
                      `note:state:${selected.id}`,
                    )
                  }
                >
                  {pending.isPending(`note:state:${selected.id}`)
                    ? "Updating…"
                    : selected.state === "open"
                      ? "Resolve"
                      : "Reopen"}
                </Button>
              </header>
              <a
                className="page-link"
                href={selected.page_url}
                target="_blank"
                rel="noreferrer"
              >
                Open live page
              </a>
              <NoteImage
                key={membership.organization_id + ":" + selected.id}
                siteId={selected.site_id}
                noteId={selected.id}
                screenshot={selected.screenshot}
              />
              {canModifySelected && (
                <form
                  className="edit-note-form"
                  key={selected.id}
                  onSubmit={(event) => void editBody(event)}
                >
                  <label htmlFor="note-body">Description</label>
                  <Textarea
                    defaultValue={selected.body}
                    id="note-body"
                    name="body"
                    readOnly={pending.isPending(`note:edit:${selected.id}`)}
                    required
                    rows={4}
                  />
                  <div>
                    <Button
                      disabled={pending.isPending(`note:edit:${selected.id}`)}
                      size="sm"
                      type="submit"
                      variant="outline"
                    >
                      {pending.isPending(`note:edit:${selected.id}`)
                        ? "Saving…"
                        : "Save description"}
                    </Button>
                    <ConfirmButton
                      description={`This permanently deletes note #${selected.number} and every reply in its thread.`}
                      label="Delete note"
                      onConfirm={() => void deleteNote()}
                      pending={pending.isPending(`note:delete:${selected.id}`)}
                      title={`Delete note #${selected.number}?`}
                    />
                  </div>
                </form>
              )}
              <div className="thread">
                {selected.replies.map((reply) => (
                  <ReplyItem
                    key={reply.id}
                    reply={reply}
                    siteId={selected.site_id}
                    noteId={selected.id}
                    csrf={me.csrf_token}
                    canModify={canModifyAuthoredContent({
                      role: membership.role,
                      userId: me.user.id,
                      authorId: reply.author,
                    })}
                    deleting={pending.isPending(`reply:delete:${reply.id}`)}
                    onSaved={loadNotes}
                    onDelete={() => void deleteReply(reply.id)}
                  />
                ))}
              </div>
              {canReplyToNote(selected.state) ? (
                <form
                  className="reply-form"
                  key={selected.id}
                  onSubmit={(event) => void addReply(event)}
                >
                  <label htmlFor="reply-body">Reply</label>
                  <Textarea
                    id="reply-body"
                    name="body"
                    readOnly={pending.isPending(`reply:create:${selected.id}`)}
                    required
                    rows={3}
                    placeholder="Add context or ask a question"
                  />
                  <Button
                    disabled={pending.isPending(`reply:create:${selected.id}`)}
                    type="submit"
                  >
                    {pending.isPending(`reply:create:${selected.id}`)
                      ? "Sending…"
                      : "Send reply"}
                  </Button>
                </form>
              ) : (
                <p className="reply-closed-message">
                  Reopen this note to add a reply.
                </p>
              )}
            </article>
          )}
        </div>
      )}
    </Page>
  );
}

function ReplyItem({
  reply,
  siteId,
  noteId,
  csrf,
  canModify,
  deleting,
  onSaved,
  onDelete,
}: {
  readonly reply: Reply;
  readonly siteId: string;
  readonly noteId: string;
  readonly csrf: string;
  readonly canModify: boolean;
  readonly deleting: boolean;
  readonly onSaved: () => Promise<void>;
  readonly onDelete: () => void;
}) {
  const api = useScopedApi();
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const editTriggerRef = useRef<HTMLButtonElement>(null);
  const restoreEditFocus = useRef(false);
  const pending = usePendingActions();
  useEffect(() => {
    if (editing) {
      editorRef.current?.focus();
      restoreEditFocus.current = true;
    } else if (restoreEditFocus.current) {
      editTriggerRef.current?.focus();
      restoreEditFocus.current = false;
    }
  }, [editing]);
  async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const body = String(new FormData(event.currentTarget).get("body") ?? "");
    await pending.run("save", async () => {
      setMessage(null);
      try {
        await api(
          `/v1/appnotes/sites/${siteId}/notes/${noteId}/replies/${reply.id}`,
          {
            method: "PATCH",
            csrf,
            body: { body },
          },
        );
        setEditing(false);
        await onSaved();
      } catch (cause) {
        if (!requestCanceled(cause)) setMessage(errorMessage(cause));
      }
    });
  }
  return (
    <div className="reply">
      <strong>{reply.author_name ?? "Reviewer"}</strong>
      {message !== null && <Notice>{message}</Notice>}
      {editing && canModify ? (
        <form className="reply-form" onSubmit={(event) => void save(event)}>
          <FieldGroup>
            <FieldRoot>
              <FieldLabel htmlFor={`edit-reply-${reply.id}`}>
                Edit reply
              </FieldLabel>
              <Textarea
                id={`edit-reply-${reply.id}`}
                ref={editorRef}
                name="body"
                defaultValue={reply.body}
                readOnly={pending.isPending("save")}
                required
                rows={3}
              />
            </FieldRoot>
          </FieldGroup>
          <div className="flex gap-2">
            <Button
              type="submit"
              size="sm"
              disabled={pending.isPending("save")}
            >
              {pending.isPending("save") ? "Saving…" : "Save reply"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={pending.isPending("save")}
              onClick={() => {
                setEditing(false);
                setMessage(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <p>{reply.body}</p>
      )}
      <time dateTime={reply.created_at}>{relativeDate(reply.created_at)}</time>
      {canModify && !editing && (
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={deleting}
            ref={editTriggerRef}
            onClick={() => setEditing(true)}
          >
            Edit reply
          </Button>
          <ConfirmButton
            description="This permanently removes the reply from this note."
            label="Delete reply"
            onConfirm={onDelete}
            pending={deleting}
            title="Delete this reply?"
            variant="ghost"
          />
        </div>
      )}
    </div>
  );
}

function AccessPage({
  focusedSiteId,
  me,
  membership,
  revision,
  onChange,
}: PageProps & { readonly focusedSiteId: string | null }) {
  const api = useScopedApi();
  const [keyInventory, setKeyInventory] = useState<Inventory<ApiKey>>({
    status: "loading",
  });
  const [siteInventory, setSiteInventory] = useState<Inventory<Site>>({
    status: "loading",
  });
  const [linkInventory, setLinkInventory] = useState<Inventory<GuestLink>>({
    status: "loading",
  });
  const [accessRevision, setAccessRevision] = useState(0);
  const sites = useMemo(
    () => (siteInventory.status === "ready" ? siteInventory.items : []),
    [siteInventory],
  );
  const keys = keyInventory.status === "ready" ? keyInventory.items : [];
  const links = linkInventory.status === "ready" ? linkInventory.items : [];
  const sitesLoaded = siteInventory.status === "ready";
  const linksLoaded = linkInventory.status === "ready";
  const recoveryStore = useMemo(createBrowserCredentialRecoveryStore, []);
  const [recoveries, setRecoveries] = useState<readonly CredentialRecovery[]>(
    () => recoveryStore.list(membership.organization_id, me.user.id),
  );
  const [revealedCredential, setRevealedCredential] =
    useState<RevealedCredential | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [keyDialogOpen, setKeyDialogOpen] = useState(false);
  const [keyCreationError, setKeyCreationError] = useState<string | null>(null);
  const [guestCreationSiteId, setGuestCreationSiteId] = useState<string | null>(
    null,
  );
  const guestCreationSite =
    sites.find((site) => site.id === guestCreationSiteId) ?? null;
  const [managedSiteId, setManagedSiteId] = useState<string | null>(
    focusedSiteId,
  );
  const pending = usePendingActions();
  const retryOperations = useRetryOperationIds();
  const administrator = canAdministerWorkspace(membership.role);
  const visibleRecoveries = useMemo(
    () => credentialRecoveriesForAccess(recoveries, administrator, sites),
    [administrator, recoveries, sites],
  );

  useEffect(() => setManagedSiteId(focusedSiteId), [focusedSiteId]);

  useEffect(() => {
    if (focusedSiteId === null || !sitesLoaded) return;
    const panel = document.getElementById(`guest-access-${focusedSiteId}`);
    panel?.focus({ preventScroll: true });
    panel?.scrollIntoView({ block: "nearest" });
  }, [focusedSiteId, sitesLoaded]);

  useEffect(() => {
    setSiteInventory({ status: "loading" });
    setKeyInventory({ status: "loading" });
    setLinkInventory({ status: "loading" });
    setRecoveries(recoveryStore.list(membership.organization_id, me.user.id));
    setRevealedCredential(null);
    setKeyDialogOpen(false);
    setKeyCreationError(null);
    setMessage(null);
  }, [administrator, me.user.id, membership.organization_id, recoveryStore]);
  useEffect(() => {
    const nextExpiry = Math.min(
      ...visibleRecoveries.map((recovery) =>
        Date.parse(recovery.recoverableUntil),
      ),
    );
    if (!Number.isFinite(nextExpiry)) return;
    const timeout = window.setTimeout(
      () => {
        const current = recoveryStore.list(
          membership.organization_id,
          me.user.id,
        );
        const currentVisible = credentialRecoveriesForAccess(
          current,
          administrator,
          sites,
        );
        setRecoveries(current);
        if (currentVisible.length < visibleRecoveries.length) {
          setMessage(
            "A credential recovery window ended. Revoke any credential you did not save, then create another.",
          );
        }
      },
      Math.max(0, nextExpiry - Date.now() + 50),
    );
    return () => window.clearTimeout(timeout);
  }, [
    me.user.id,
    administrator,
    membership.organization_id,
    recoveryStore,
    sites,
    visibleRecoveries,
  ]);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    async function loadInventory<T>(
      request: () => Promise<readonly T[]>,
      setInventory: React.Dispatch<React.SetStateAction<Inventory<T>>>,
    ): Promise<void> {
      setInventory((current) =>
        current.status === "ready" ? current : { status: "loading" },
      );
      try {
        const items = await request();
        if (active) setInventory({ status: "ready", items });
      } catch (cause) {
        if (active)
          setInventory({ status: "error", message: errorMessage(cause) });
      }
    }

    const input = { signal: controller.signal };
    void loadInventory(
      async () =>
        (
          await api<{ sites: Site[] }>(
            `/v1/orgs/${membership.organization_id}/appnotes/sites?limit=100`,
            input,
          )
        ).sites,
      setSiteInventory,
    );
    void loadInventory(
      async () =>
        (
          await api<{ guest_links: GuestLink[] }>(
            `/v1/orgs/${membership.organization_id}/appnotes/guest-links`,
            input,
          )
        ).guest_links,
      setLinkInventory,
    );
    if (administrator) {
      void loadInventory(
        async () =>
          (
            await api<{ api_keys: ApiKey[] }>(
              `/v1/orgs/${membership.organization_id}/api-keys`,
              input,
            )
          ).api_keys,
        setKeyInventory,
      );
    } else {
      setKeyInventory({ status: "ready", items: [] });
    }
    return () => {
      active = false;
      controller.abort();
    };
  }, [administrator, membership.organization_id, revision, accessRevision]);

  async function createKey(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const action = "api-key:create";
    const name = String(data.get("name") ?? "");
    const agentName = String(data.get("agent_name") ?? "");
    const retryAction = `${action}:${JSON.stringify([name, agentName])}`;
    const operationId = retryOperations.get(retryAction);
    const recovery = recoveryStore.beginApiKey({
      agentName: agentName || null,
      name,
      operationId,
      organizationId: membership.organization_id,
      userId: me.user.id,
    });
    setRecoveries(recoveryStore.list(membership.organization_id, me.user.id));
    await pending.run(action, async () => {
      try {
        const result = await api<{
          plaintext: string;
          recoverable_until: string;
        }>(`/v1/orgs/${membership.organization_id}/api-keys`, {
          method: "POST",
          csrf: me.csrf_token,
          idempotencyKey: operationId,
          body: {
            name,
            agent_name: agentName || null,
          },
        });
        const current = recoveryStore.setDeadline(
          recovery,
          result.recoverable_until,
        );
        setRecoveries(
          recoveryStore.list(membership.organization_id, me.user.id),
        );
        setRevealedCredential({
          label: "Copy this API key",
          recovery: current,
          value: result.plaintext,
        });
        setKeyCreationError(null);
        setKeyDialogOpen(false);
        form.reset();
        onChange();
      } catch (cause) {
        if (credentialRecoveryCannotContinue(cause)) {
          recoveryStore.remove(recovery.id);
          retryOperations.complete(retryAction);
          setRecoveries(
            recoveryStore.list(membership.organization_id, me.user.id),
          );
        }
        setKeyCreationError(errorMessage(cause));
      }
    });
  }

  async function createGuestLink(
    site: Site,
    expiresAt: string | null,
  ): Promise<void> {
    if (!site.enabled) {
      setMessage("Restore this Site before creating guest access.");
      return;
    }
    const action = `guest-link:create:${site.id}`;
    await pending.run(action, async () => {
      const operationId = retryOperations.get(action);
      const recovery = recoveryStore.beginGuestLink({
        operationId,
        organizationId: membership.organization_id,
        siteId: site.id,
        siteName: site.name,
        userId: me.user.id,
        expiresAt,
      });
      setRecoveries(recoveryStore.list(membership.organization_id, me.user.id));
      setGuestCreationSiteId(null);
      try {
        const result = await api<{
          guest_link: {
            recoverable_until: string;
            share_url: string;
          };
        }>(`/v1/appnotes/sites/${site.id}/guest-links`, {
          method: "POST",
          csrf: me.csrf_token,
          idempotencyKey: operationId,
          body: { expires_at: recovery.expiresAt },
        });
        const current = recoveryStore.setDeadline(
          recovery,
          result.guest_link.recoverable_until,
        );
        setRecoveries(
          recoveryStore.list(membership.organization_id, me.user.id),
        );
        setRevealedCredential({
          label: `Copy the guest link for ${site.name}`,
          recovery: current,
          value: result.guest_link.share_url,
        });
        setMessage(null);
        onChange();
      } catch (cause) {
        if (credentialRecoveryCannotContinue(cause)) {
          recoveryStore.remove(recovery.id);
          retryOperations.complete(action);
          setRecoveries(
            recoveryStore.list(membership.organization_id, me.user.id),
          );
        }
        setMessage(errorMessage(cause));
      }
    });
  }

  async function recoverCredential(
    recovery: CredentialRecovery,
  ): Promise<void> {
    if (
      recovery.kind === "guest_link" &&
      !sites.some((site) => site.id === recovery.siteId && site.enabled)
    ) {
      setMessage("Restore this Site before finishing its guest link.");
      return;
    }
    await pending.run(`credential:recover:${recovery.id}`, async () => {
      try {
        if (recovery.kind === "api_key") {
          const result = await api<{
            plaintext: string;
            recoverable_until: string;
          }>(`/v1/orgs/${recovery.organizationId}/api-keys`, {
            method: "POST",
            csrf: me.csrf_token,
            idempotencyKey: recovery.operationId,
            body: {
              name: recovery.name,
              agent_name: recovery.agentName,
            },
          });
          const current = recoveryStore.setDeadline(
            recovery,
            result.recoverable_until,
          );
          setRevealedCredential({
            label: "Copy this API key",
            recovery: current,
            value: result.plaintext,
          });
        } else {
          const result = await api<{
            guest_link: {
              recoverable_until: string;
              share_url: string;
            };
          }>(`/v1/appnotes/sites/${recovery.siteId}/guest-links`, {
            method: "POST",
            csrf: me.csrf_token,
            idempotencyKey: recovery.operationId,
            body: { expires_at: recovery.expiresAt },
          });
          const current = recoveryStore.setDeadline(
            recovery,
            result.guest_link.recoverable_until,
          );
          setRevealedCredential({
            label: `Copy the guest link for ${recovery.siteName}`,
            recovery: current,
            value: result.guest_link.share_url,
          });
        }
        setRecoveries(
          recoveryStore.list(membership.organization_id, me.user.id),
        );
        setMessage(null);
        onChange();
      } catch (cause) {
        if (credentialRecoveryCannotContinue(cause)) {
          recoveryStore.remove(recovery.id);
          retryOperations.complete(credentialRetryAction(recovery));
          setRecoveries(
            recoveryStore.list(membership.organization_id, me.user.id),
          );
        }
        setMessage(errorMessage(cause));
      }
    });
  }

  function acknowledgeCredential(recovery: CredentialRecovery): void {
    recoveryStore.remove(recovery.id);
    retryOperations.complete(credentialRetryAction(recovery));
    setRecoveries(recoveryStore.list(membership.organization_id, me.user.id));
    setRevealedCredential(null);
  }

  async function revokeKey(key: ApiKey): Promise<void> {
    await pending.run(`api-key:revoke:${key.id}`, async () => {
      try {
        await api(`/v1/orgs/${membership.organization_id}/api-keys/${key.id}`, {
          method: "DELETE",
          csrf: me.csrf_token,
        });
        onChange();
      } catch (cause) {
        setMessage(errorMessage(cause));
      }
    });
  }

  async function revokeGuestLink(link: GuestLink): Promise<void> {
    await pending.run(`guest-link:revoke:${link.id}`, async () => {
      try {
        await api(`/v1/appnotes/sites/${link.site_id}/guest-links/${link.id}`, {
          method: "DELETE",
          csrf: me.csrf_token,
        });
        onChange();
      } catch (cause) {
        setMessage(errorMessage(cause));
      }
    });
  }

  const linksBySite = new Map<string, GuestLink[]>();
  for (const link of links) {
    const siteLinks = linksBySite.get(link.site_id) ?? [];
    siteLinks.push(link);
    linksBySite.set(link.site_id, siteLinks);
  }

  return (
    <Page title="Access">
      {me.mcp_available === true && (
        <ConnectedAgents key={me.user.id} me={me} />
      )}
      {message !== null && <Notice>{message}</Notice>}
      <Dialog
        open={guestCreationSite !== null}
        onOpenChange={(open) => {
          if (!open) setGuestCreationSiteId(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create guest link</DialogTitle>
            <DialogDescription>
              {guestCreationSite?.name}. Anyone with this link can join the site
              review. Choose when the link stops accepting guests.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (guestCreationSite === null) return;
              const duration = new FormData(event.currentTarget).get("expiry");
              const days = duration === "7" ? 7 : duration === "30" ? 30 : null;
              const expiresAt =
                days === null
                  ? null
                  : new Date(Date.now() + days * 86_400_000).toISOString();
              void createGuestLink(guestCreationSite, expiresAt);
            }}
          >
            <FieldGroup className="gap-5">
              <FieldRoot>
                <FieldLabel htmlFor="guest-link-expiry">
                  Link expires
                </FieldLabel>
                <NativeSelect
                  id="guest-link-expiry"
                  name="expiry"
                  defaultValue="never"
                >
                  <NativeSelectOption value="never">
                    Never (until revoked)
                  </NativeSelectOption>
                  <NativeSelectOption value="7">In 7 days</NativeSelectOption>
                  <NativeSelectOption value="30">In 30 days</NativeSelectOption>
                </NativeSelect>
              </FieldRoot>
              <DialogFooter>
                <Button type="submit">Create guest link</Button>
              </DialogFooter>
            </FieldGroup>
          </form>
        </DialogContent>
      </Dialog>
      {visibleRecoveries.length > 0 && (
        <CredentialRecoveryQueue
          pending={pending}
          recoveries={visibleRecoveries}
          onRecover={(recovery) => void recoverCredential(recovery)}
        />
      )}
      <div className="access-stack">
        <ConsoleSection
          description="Create and manage guest review links for each registered site."
          title="Guest access"
          titleId="guest-links-title"
        >
          {linkInventory.status === "error" && (
            <Notice>
              Guest links could not be loaded. {linkInventory.message}{" "}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setAccessRevision((value) => value + 1)}
              >
                Retry guest links
              </Button>
            </Notice>
          )}
          {siteInventory.status === "error" ? (
            <Notice>
              Registered sites could not be loaded. {siteInventory.message}{" "}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setAccessRevision((value) => value + 1)}
              >
                Retry sites
              </Button>
            </Notice>
          ) : !sitesLoaded ? (
            <div className="access-inventory">
              <p className="access-empty">Loading registered web apps…</p>
            </div>
          ) : sites.length === 0 ? (
            <div className="access-inventory">
              <div className="access-inventory-heading">
                <div>
                  <h3>No web apps registered</h3>
                  <p>
                    {administrator
                      ? "Add a web app from Sites before creating guest access."
                      : "A team administrator must add a web app before you can create guest access."}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <table className="guest-site-table">
              <thead>
                <tr>
                  <th scope="col">Web app</th>
                  <th scope="col">Guest links</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sites.map((site) => {
                  const siteLinks = linksBySite.get(site.id) ?? [];
                  const managedLinks = [...siteLinks].sort(
                    (left, right) =>
                      guestLinkStateRank(left) - guestLinkStateRank(right),
                  );
                  const currentLinks = managedLinks.filter(
                    (link) => guestLinkState(link) === "Active",
                  );
                  const historicalLinks = managedLinks.filter(
                    (link) => guestLinkState(link) !== "Active",
                  );
                  const activeLinkCount = site.enabled
                    ? currentLinks.length
                    : 0;
                  const managed = managedSiteId === site.id;
                  const unfinishedLink = visibleRecoveries.find(
                    (recovery) =>
                      recovery.kind === "guest_link" &&
                      recovery.siteId === site.id,
                  );
                  const createAction = `guest-link:create:${site.id}`;
                  const recoverAction =
                    unfinishedLink === undefined
                      ? null
                      : `credential:recover:${unfinishedLink.id}`;
                  const creating = pending.isPending(createAction);
                  const recovering =
                    recoverAction !== null && pending.isPending(recoverAction);
                  return (
                    <Fragment key={site.id}>
                      <tr data-managed={managed}>
                        <td>
                          <div className="guest-site-identity">
                            <span
                              className="guest-site-icon"
                              aria-hidden="true"
                            >
                              <HugeiconsIcon
                                icon={WebDesign01Icon}
                                strokeWidth={1.8}
                              />
                            </span>
                            <span>
                              <strong>{site.name}</strong>
                              <small>{site.origin}</small>
                            </span>
                          </div>
                        </td>
                        <td>
                          <div
                            className="guest-site-status"
                            data-active={activeLinkCount > 0}
                          >
                            <strong>
                              {!linksLoaded
                                ? linkInventory.status === "loading"
                                  ? "Loading links…"
                                  : "Links unavailable"
                                : !site.enabled
                                  ? "Site archived"
                                  : activeLinkCount === 0
                                    ? "No active links"
                                    : `${activeLinkCount} active ${activeLinkCount === 1 ? "link" : "links"}`}
                            </strong>
                            <small>
                              {!linksLoaded
                                ? ""
                                : !site.enabled
                                  ? currentLinks.length === 0
                                    ? "Guest access is paused"
                                    : `${currentLinks.length} ${currentLinks.length === 1 ? "link" : "links"} paused`
                                  : siteLinks.length === 0
                                    ? "None created yet"
                                    : guestLinkHistorySummary(historicalLinks)}
                            </small>
                          </div>
                        </td>
                        <td>
                          <div className="guest-site-actions">
                            <Button
                              aria-controls={`guest-access-${site.id}`}
                              aria-expanded={managed}
                              size="sm"
                              type="button"
                              variant="ghost"
                              onClick={() =>
                                setManagedSiteId(managed ? null : site.id)
                              }
                            >
                              {managed ? "Close" : "Manage"}
                            </Button>
                            {site.enabled ? (
                              <Button
                                aria-label={`Create guest link for ${site.name}`}
                                disabled={creating || recovering}
                                size="sm"
                                type="button"
                                onClick={() =>
                                  unfinishedLink === undefined
                                    ? setGuestCreationSiteId(site.id)
                                    : void recoverCredential(unfinishedLink)
                                }
                              >
                                {creating
                                  ? "Creating…"
                                  : recovering
                                    ? "Recovering…"
                                    : unfinishedLink === undefined
                                      ? "Create link"
                                      : "Finish link"}
                              </Button>
                            ) : (
                              <StatusBadge>Archived</StatusBadge>
                            )}
                          </div>
                        </td>
                      </tr>
                      {managed && (
                        <tr className="guest-site-detail-row">
                          <td colSpan={3}>
                            <section
                              aria-label={`Guest links for ${site.name}`}
                              className="guest-site-detail"
                              id={`guest-access-${site.id}`}
                              tabIndex={-1}
                            >
                              <header className="guest-site-detail-header">
                                <div>
                                  <h3>Guest access for {site.name}</h3>
                                  <p>{site.origin}</p>
                                </div>
                                <StatusBadge>
                                  {!linksLoaded
                                    ? "Unknown"
                                    : site.enabled
                                      ? `${activeLinkCount} active`
                                      : "Archived"}
                                </StatusBadge>
                              </header>
                              {!site.enabled && currentLinks.length > 0 && (
                                <Notice>
                                  Existing guest links are paused. Restore the
                                  Site to use them again, or revoke them below.
                                </Notice>
                              )}
                              {!linksLoaded ? (
                                <p className="guest-link-empty">
                                  {linkInventory.status === "loading"
                                    ? "Loading guest links…"
                                    : "Retry loading guest links to see access and history."}
                                </p>
                              ) : currentLinks.length === 0 ? (
                                <p className="guest-link-empty">
                                  {site.enabled
                                    ? "No active guest links for this site."
                                    : "This archived Site has no current guest links."}
                                </p>
                              ) : (
                                <ul className="guest-link-list">
                                  {currentLinks.map((link) => (
                                    <GuestLinkItem
                                      key={link.id}
                                      link={link}
                                      siteEnabled={site.enabled}
                                      pending={pending.isPending(
                                        `guest-link:revoke:${link.id}`,
                                      )}
                                      onRevoke={() =>
                                        void revokeGuestLink(link)
                                      }
                                    />
                                  ))}
                                </ul>
                              )}
                              {historicalLinks.length > 0 && (
                                <details className="guest-link-history">
                                  <summary>
                                    <span>History</span>
                                    <StatusBadge>
                                      {historicalLinks.length}
                                    </StatusBadge>
                                  </summary>
                                  <ul className="guest-link-list guest-link-history-list">
                                    {historicalLinks.map((link) => (
                                      <GuestLinkItem
                                        key={link.id}
                                        link={link}
                                      />
                                    ))}
                                  </ul>
                                </details>
                              )}
                            </section>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </ConsoleSection>
        <ConsoleSection
          actions={
            administrator ? (
              <Dialog
                open={keyDialogOpen}
                onOpenChange={(open) => {
                  setKeyDialogOpen(open);
                  if (open) setKeyCreationError(null);
                }}
              >
                <DialogTrigger render={<Button type="button" />}>
                  Create key
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create API key</DialogTitle>
                    <DialogDescription>
                      Create a credential for an agent or integration. The
                      plaintext key is shown once.
                    </DialogDescription>
                  </DialogHeader>
                  {keyCreationError !== null && (
                    <Notice>{keyCreationError}</Notice>
                  )}
                  <form onSubmit={(event) => void createKey(event)}>
                    <FieldGroup className="gap-5">
                      <Field
                        label="Key name"
                        name="name"
                        placeholder="Design QA agent"
                        required
                      />
                      <Field
                        label="Agent name (optional)"
                        name="agent_name"
                        placeholder="Codex"
                      />
                      <DialogFooter>
                        <Button
                          disabled={pending.isPending("api-key:create")}
                          type="submit"
                        >
                          {pending.isPending("api-key:create")
                            ? "Creating…"
                            : "Create key"}
                        </Button>
                      </DialogFooter>
                    </FieldGroup>
                  </form>
                </DialogContent>
              </Dialog>
            ) : undefined
          }
          title="API keys"
          titleId="api-keys-title"
        >
          {administrator ? (
            <>
              {keyInventory.status === "error" ? (
                <Notice>
                  API keys could not be loaded. {keyInventory.message}{" "}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setAccessRevision((value) => value + 1)}
                  >
                    Retry API keys
                  </Button>
                </Notice>
              ) : keyInventory.status === "loading" ? (
                <div className="access-inventory">
                  <p className="access-empty">Loading API keys…</p>
                </div>
              ) : keys.length === 0 ? (
                <div className="access-inventory">
                  <p className="access-empty">No API keys yet.</p>
                </div>
              ) : (
                <div className="api-key-table">
                  <div className="api-key-table-header" aria-hidden="true">
                    <span>Key</span>
                    <span>Last used</span>
                    <span />
                  </div>
                  <ul className="api-key-list">
                    {keys.map((key) => (
                      <li className="api-key-row" key={key.id}>
                        <div className="api-key-identity">
                          <strong>{key.name}</strong>
                          <span>
                            {key.agent_name === null
                              ? "No agent assigned"
                              : `Agent: ${key.agent_name}`}
                            {key.revoked_at === null ? "" : " · Revoked"}
                          </span>
                        </div>
                        <time>
                          {key.last_used_at === null
                            ? "Never used"
                            : `Used ${relativeDate(key.last_used_at)}`}
                        </time>
                        {key.revoked_at === null && (
                          <ConfirmButton
                            description={`Agents using ${key.name} will immediately lose access.`}
                            label="Revoke"
                            onConfirm={() => void revokeKey(key)}
                            pending={pending.isPending(
                              `api-key:revoke:${key.id}`,
                            )}
                            title={`Revoke ${key.name}?`}
                            variant="ghost"
                          />
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <div className="access-restricted">
              <Notice>Only team administrators can manage API keys.</Notice>
            </div>
          )}
        </ConsoleSection>
      </div>
      <Dialog
        open={revealedCredential !== null}
        onOpenChange={(open) => {
          if (!open) setRevealedCredential(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save this credential</DialogTitle>
            <DialogDescription>
              AppNotes does not store the plaintext. If you close this dialog,
              you can reopen it from Access until the recovery window ends.
            </DialogDescription>
          </DialogHeader>
          {revealedCredential !== null && (
            <>
              <SecretReveal
                key={revealedCredential.recovery.operationId}
                label={revealedCredential.label}
                value={revealedCredential.value}
              />
              <DialogFooter>
                <Button
                  type="button"
                  onClick={() =>
                    acknowledgeCredential(revealedCredential.recovery)
                  }
                >
                  I saved it
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Page>
  );
}

function BillingPage({ me, membership }: Pick<PageProps, "me" | "membership">) {
  const api = useScopedApi();
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<"checkout" | "portal" | "resync" | null>(
    null,
  );
  const [message, setMessage] = useState<string | null>(null);
  const administrator = membership.role !== "member";
  const responseGuardRef = useRef<OrganizationResponseGuard | null>(null);
  responseGuardRef.current ??= new OrganizationResponseGuard(
    membership.organization_id,
  );
  const responseGuard = responseGuardRef.current;

  async function load(): Promise<void> {
    const request = responseGuard.begin();
    try {
      const nextSummary = await api<BillingSummary>(
        `/v1/orgs/${membership.organization_id}/billing`,
        { signal: request.signal },
      );
      if (!responseGuard.owns(request)) return;
      setSummary(nextSummary);
      setMessage(null);
    } catch (cause) {
      if (!responseGuard.owns(request)) return;
      setMessage(errorMessage(cause));
    } finally {
      if (responseGuard.owns(request)) setLoading(false);
    }
  }

  useEffect(() => {
    responseGuard.selectOrganization(membership.organization_id);
    setSummary(null);
    setMessage(null);
    setBusy(null);
    setLoading(true);
    void load();
    return () => responseGuard.invalidate();
  }, [membership.organization_id]);

  async function redirectToBilling(
    action: "checkout" | "portal",
  ): Promise<void> {
    setBusy(action);
    try {
      const result = await api<{ readonly url: string }>(
        `/v1/orgs/${membership.organization_id}/billing/${action}`,
        { method: "POST", csrf: me.csrf_token },
      );
      window.location.assign(result.url);
    } catch (cause) {
      setMessage(errorMessage(cause));
      setBusy(null);
    }
  }

  async function resync(): Promise<void> {
    setBusy("resync");
    try {
      setSummary(
        await api<BillingSummary>(
          `/v1/orgs/${membership.organization_id}/billing/resync`,
          { method: "POST", csrf: me.csrf_token },
        ),
      );
      setMessage("Billing status refreshed.");
    } catch (cause) {
      setMessage(errorMessage(cause));
    } finally {
      setBusy(null);
    }
  }

  if (loading) {
    return (
      <Page title="Billing">
        <LoadingRows />
      </Page>
    );
  }
  if (summary === null) {
    return (
      <Page title="Billing">
        <StatePanel
          action={<Button onClick={() => void load()}>Try again</Button>}
          description={message ?? "Billing status could not be loaded."}
          title="Billing unavailable"
        />
      </Page>
    );
  }

  const team = summary.offer_id === "appnotes_team_v1";
  const cap = summary.active_site_cap;
  const capacityUnits =
    team && cap !== null ? Math.max(1, Math.ceil(cap / 25)) : 0;
  const monthlyPrice = 79 * capacityUnits;
  const usagePercent =
    cap === null || cap === 0
      ? 0
      : Math.min(100, (summary.active_sites_this_month / cap) * 100);
  const periodLabel = summary.cancel_at_period_end ? "Access ends" : "Renews";
  const failedEvents = summary.failed_events ?? [];

  return (
    <Page title="Billing">
      {message !== null ? <Notice>{message}</Notice> : null}
      {summary.billing_state === "grace" ? (
        <Alert variant="destructive">
          <AlertTitle>Payment needs attention</AlertTitle>
          <AlertDescription>
            Team remains active through{" "}
            {calendarDate(summary.grace_ends_at ?? "")}. Update the payment
            method before then to avoid falling back to Free.
          </AlertDescription>
        </Alert>
      ) : null}
      {summary.has_conflict ? (
        <Alert variant="destructive">
          <AlertTitle>Billing needs review</AlertTitle>
          <AlertDescription>
            More than one AppNotes subscription was found. Access is preserved,
            but Checkout is blocked until support resolves the duplicate.
          </AlertDescription>
        </Alert>
      ) : null}
      {failedEvents.length > 0 ? (
        <Alert variant="destructive">
          <AlertTitle>Billing update needs attention</AlertTitle>
          <AlertDescription>
            {failedEvents.length === 1
              ? "One Stripe update could not be reconciled."
              : failedEvents.length +
                " Stripe updates could not be reconciled."}{" "}
            Your current AppNotes access is unchanged. Refresh the status to
            repair the local projection.
            <details className="billing-failed-events">
              <summary>View failed updates</summary>
              <ul>
                {failedEvents.map((event) => (
                  <li key={event.id}>
                    <strong>{event.kind}</strong>
                    <span>{calendarDate(event.received_at)}</span>
                  </li>
                ))}
              </ul>
            </details>
          </AlertDescription>
        </Alert>
      ) : null}
      <ConsoleSection
        actions={
          <StatusBadge tone={team ? "primary" : "neutral"}>
            Current plan
          </StatusBadge>
        }
        title={team ? "Team" : "Free"}
        titleId="billing-plan-title"
      >
        <div className="billing-overview">
          <div className="billing-price">
            <strong>{team ? `$${monthlyPrice}` : "$0"}</strong>
            <span>
              {team
                ? `${capacityUnits} × 25-Site capacity ${capacityUnits === 1 ? "block" : "blocks"}, billed monthly`
                : "free forever"}
            </span>
          </div>
          <div className="billing-usage">
            <div className="billing-usage-heading">
              <strong>Active Sites this month</strong>
              <span>
                {summary.active_sites_this_month} of {cap ?? "unlimited"}
              </span>
            </div>
            <div
              aria-label={`${summary.active_sites_this_month} of ${cap ?? "unlimited"} active Sites used`}
              aria-valuemax={cap ?? undefined}
              aria-valuemin={0}
              aria-valuenow={summary.active_sites_this_month}
              className="billing-meter"
              role="progressbar"
            >
              <span style={{ width: `${usagePercent}%` }} />
            </div>
            <p>
              A Site counts only after it receives an annotation during the
              current UTC month. Registered and archived Sites are unlimited.
            </p>
          </div>
          <dl className="billing-facts">
            <div>
              <dt>People</dt>
              <dd>{team ? "Unlimited members" : "One owner"}</dd>
            </div>
            <div>
              <dt>Guests and agents</dt>
              <dd>Unlimited</dd>
            </div>
            {team ? (
              <div>
                <dt>Additional capacity</dt>
                <dd>$79 for each additional 25 active Sites</dd>
              </div>
            ) : null}
            {team && summary.renews_at !== null ? (
              <div>
                <dt>{periodLabel}</dt>
                <dd>{calendarDate(summary.renews_at)}</dd>
              </div>
            ) : null}
          </dl>
          {administrator ? (
            <div className="billing-actions">
              {summary.can_upgrade ? (
                <Button
                  disabled={busy !== null}
                  onClick={() => void redirectToBilling("checkout")}
                  type="button"
                >
                  {busy === "checkout"
                    ? "Opening Checkout…"
                    : "Upgrade to Team"}
                </Button>
              ) : summary.can_manage_billing ? (
                <Button
                  disabled={busy !== null}
                  onClick={() => void redirectToBilling("portal")}
                  type="button"
                >
                  {busy === "portal"
                    ? "Opening portal…"
                    : "Manage plan and capacity"}
                </Button>
              ) : null}
              <Button
                disabled={busy !== null}
                onClick={() => void resync()}
                type="button"
                variant="outline"
              >
                {busy === "resync" ? "Refreshing…" : "Refresh status"}
              </Button>
            </div>
          ) : null}
        </div>
      </ConsoleSection>
    </Page>
  );
}

function TeamPage({ me, membership, onChange }: Omit<PageProps, "revision">) {
  const api = useScopedApi();
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState<readonly TeamMember[]>([]);
  const [management, setManagement] = useState<{
    readonly organizationId: string;
    readonly mode: MembershipManagement;
  } | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const pending = usePendingActions();
  const responseGuardRef = useRef<OrganizationResponseGuard | null>(null);
  responseGuardRef.current ??= new OrganizationResponseGuard(
    membership.organization_id,
  );
  const responseGuard = responseGuardRef.current;
  const managementMode =
    management?.organizationId === membership.organization_id
      ? management.mode
      : null;
  const canAdministerMembers = canManageMembers(
    membership.role,
    managementMode,
  );

  async function load(): Promise<void> {
    const request = responseGuard.begin();
    setLoading(true);
    try {
      const result = await api<{
        members: TeamMember[];
        membership_management: MembershipManagement;
      }>(`/v1/orgs/${membership.organization_id}/members`, {
        signal: request.signal,
      });
      if (!responseGuard.owns(request)) return;
      setMembers(result.members);
      setManagement({
        organizationId: membership.organization_id,
        mode: result.membership_management,
      });
      setMessage(null);
    } catch (cause) {
      if (!responseGuard.owns(request)) return;
      setMessage(errorMessage(cause));
    } finally {
      if (responseGuard.owns(request)) setLoading(false);
    }
  }
  useEffect(() => {
    responseGuard.selectOrganization(membership.organization_id);
    setMembers([]);
    setManagement(null);
    setMessage(null);
    void load();
    return () => responseGuard.invalidate();
  }, [membership.organization_id]);

  async function invite(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    await pending.run("member:invite", async () => {
      try {
        await api(
          `/v1/orgs/${membership.organization_id}/members/invitations`,
          {
            method: "POST",
            csrf: me.csrf_token,
            body: { email: data.get("email"), role: data.get("role") },
          },
        );
        form.reset();
        setMessage("Invitation sent.");
      } catch (cause) {
        setMessage(errorMessage(cause));
      }
    });
  }

  async function changeRole(
    userId: string,
    role: "admin" | "member",
  ): Promise<void> {
    await pending.run(`member:role:${userId}`, async () => {
      try {
        await api(`/v1/orgs/${membership.organization_id}/members/${userId}`, {
          method: "PATCH",
          csrf: me.csrf_token,
          body: { role },
        });
        await load();
        onChange();
      } catch (cause) {
        setMessage(errorMessage(cause));
      }
    });
  }

  async function resync(): Promise<void> {
    await pending.run("identity:resync", async () => {
      try {
        await api(`/v1/orgs/${membership.organization_id}/identity/resync`, {
          method: "POST",
          csrf: me.csrf_token,
        });
        await load();
        setMessage("Directory synchronized.");
      } catch (cause) {
        setMessage(errorMessage(cause));
      }
    });
  }

  async function removeMember(member: TeamMember): Promise<void> {
    await pending.run(`member:remove:${member.user_id}`, async () => {
      try {
        await api(
          `/v1/orgs/${membership.organization_id}/members/${member.user_id}`,
          { method: "DELETE", csrf: me.csrf_token },
        );
        await load();
        onChange();
      } catch (cause) {
        setMessage(errorMessage(cause));
      }
    });
  }

  return (
    <Page title="Team">
      {message !== null && <Notice>{message}</Notice>}
      {canAdministerMembers && (
        <ConsoleSection title="Invite people" titleId="team-invitations-title">
          <div className="team-admin-stack">
            <form
              className="team-invite-form"
              onSubmit={(event) => void invite(event)}
            >
              <FieldGroup className="grid gap-5 md:grid-cols-[minmax(0,1fr)_12rem_auto] md:items-end">
                <Field
                  label="Invite by email"
                  name="email"
                  type="email"
                  placeholder="reviewer@example.com"
                  required
                />
                <label className="field">
                  <span>Role</span>
                  <NativeSelect name="role" defaultValue="member">
                    <NativeSelectOption value="member">
                      Member
                    </NativeSelectOption>
                    <NativeSelectOption value="admin">
                      Administrator
                    </NativeSelectOption>
                  </NativeSelect>
                </label>
                <Button
                  disabled={pending.isPending("member:invite")}
                  type="submit"
                >
                  {pending.isPending("member:invite")
                    ? "Sending…"
                    : "Send invitation"}
                </Button>
              </FieldGroup>
            </form>
            <div className="directory-sync-row">
              <div>
                <h3>Directory sync</h3>
                <p>
                  Refresh members and roles from the configured identity
                  provider.
                </p>
              </div>
              <Button
                disabled={pending.isPending("identity:resync")}
                type="button"
                variant="outline"
                onClick={() => void resync()}
              >
                {pending.isPending("identity:resync") ? "Syncing…" : "Sync now"}
              </Button>
            </div>
          </div>
        </ConsoleSection>
      )}
      <ConsoleSection
        actions={
          loading ? undefined : managementMode === null ? undefined : (
            <StatusBadge>
              {members.length} {members.length === 1 ? "member" : "members"}
            </StatusBadge>
          )
        }
        description={
          managementMode === "identity_provider"
            ? "Members and roles are managed through your login provider. Everyone listed here can see this team's AppNotes content."
            : "Everyone with access to this team can see its AppNotes content."
        }
        title="Members"
        titleId="team-members-title"
      >
        {loading ? (
          <LoadingRows />
        ) : managementMode === null ? (
          <Button type="button" variant="outline" onClick={() => void load()}>
            Retry loading members
          </Button>
        ) : members.length === 0 ? (
          <p>No members found.</p>
        ) : null}
        <ul className="member-list" aria-busy={loading}>
          {members.map((member) => (
            <li key={member.user_id}>
              <AvatarForUser
                name={member.display_name}
                url={member.avatar_url}
              />
              <strong>{member.display_name}</strong>
              {canAdministerMember({
                actorRole: membership.role,
                actorUserId: me.user.id,
                management: managementMode,
                memberRole: member.role,
                memberUserId: member.user_id,
              }) ? (
                <div className="member-actions">
                  <NativeSelect
                    aria-label={`Role for ${member.display_name}`}
                    value={member.role}
                    disabled={pending.isPending(
                      `member:role:${member.user_id}`,
                    )}
                    onChange={(event) => {
                      const role = event.target.value;
                      if (role === "admin" || role === "member") {
                        void changeRole(member.user_id, role);
                      }
                    }}
                  >
                    <NativeSelectOption value="member">
                      Member
                    </NativeSelectOption>
                    <NativeSelectOption value="admin">
                      Administrator
                    </NativeSelectOption>
                  </NativeSelect>
                  <ConfirmButton
                    description={`${member.display_name} will lose access to this team and its AppNotes content.`}
                    label="Remove"
                    onConfirm={() => void removeMember(member)}
                    pending={pending.isPending(
                      `member:remove:${member.user_id}`,
                    )}
                    title={`Remove ${member.display_name}?`}
                  />
                </div>
              ) : member.role === "owner" ? (
                <StatusBadge tone="primary">Owner</StatusBadge>
              ) : (
                <span>{member.role}</span>
              )}
            </li>
          ))}
        </ul>
      </ConsoleSection>
    </Page>
  );
}

interface PageProps {
  readonly me: Me;
  readonly membership: Membership;
  readonly revision: number;
  readonly onChange: () => void;
}

function OrganizationGate({
  me,
  onChange,
  onSessionError,
}: {
  readonly me: Me;
  readonly onChange: (me: Me | null) => void;
  readonly onSessionError: (message: string) => void;
}) {
  const api = useScopedApi();
  const [message, setMessage] = useState<string | null>(null);
  const pending = usePendingActions();
  async function select(organizationId: string): Promise<void> {
    await pending.run("organization:select", async () => {
      setMessage(null);
      const result = await selectOrganizationSession(
        api,
        organizationId,
        me.csrf_token,
      );
      switch (result.status) {
        case "confirmed":
          onChange(result.me);
          setMessage(result.notice);
          break;
        case "signed_out":
          onChange(null);
          break;
        case "unavailable":
          onSessionError(result.message);
          break;
        case "cancelled":
          break;
      }
    });
  }
  async function create(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const name = new FormData(event.currentTarget).get("name");
    await pending.run("organization:create", async () => {
      try {
        const result = await api<{ organization_id: string }>("/v1/orgs", {
          method: "POST",
          csrf: me.csrf_token,
          body: { name },
        });
        await select(result.organization_id);
      } catch (cause) {
        setMessage(errorMessage(cause));
      }
    });
  }
  return (
    <div className="center-screen">
      <Brand />
      <section className="gate-card">
        <p className="eyebrow">Welcome, {me.user.display_name}</p>
        <h1>Choose your team</h1>
        <p>AppNotes keeps sites, feedback, and access scoped to one team.</p>
        {message !== null && <Notice>{message}</Notice>}
        <div className="organization-list">
          {me.memberships.map((membership) => (
            <button
              className="organization-button"
              disabled={pending.isPending("organization:select")}
              key={membership.organization_id}
              type="button"
              onClick={() => void select(membership.organization_id)}
            >
              <strong>{membership.organization_name}</strong>
              <span>{membership.role}</span>
            </button>
          ))}
        </div>
        <form onSubmit={(event) => void create(event)}>
          <FieldGroup className="mt-8 gap-5 border-t pt-6">
            <Field
              label="Or create a team"
              name="name"
              placeholder="Acme product"
              required
            />
            <Button
              disabled={pending.isPending("organization:create")}
              type="submit"
            >
              {pending.isPending("organization:create")
                ? "Creating…"
                : "Create team"}
            </Button>
          </FieldGroup>
        </form>
        {me.mcp_available === true && (
          <div className="mt-8">
            <ConnectedAgents key={me.user.id} me={me} />
          </div>
        )}
      </section>
    </div>
  );
}

function Page({
  actions,
  title,
  children,
}: {
  readonly actions?: React.ReactNode;
  readonly title: string;
  readonly children: React.ReactNode;
}) {
  return (
    <div className="page-stack">
      <PageHeader actions={actions} title={title} />
      {children}
    </div>
  );
}
function NavButton({
  active,
  icon,
  onClick,
  children,
}: {
  readonly active: boolean;
  readonly icon: IconSvgElement;
  readonly onClick: () => void;
  readonly children: React.ReactNode;
}) {
  return (
    <Button
      className="workspace-nav-button justify-start max-[900px]:justify-center"
      size="sm"
      variant="ghost"
      type="button"
      aria-current={active ? "page" : undefined}
      onClick={onClick}
    >
      <HugeiconsIcon data-icon="inline-start" icon={icon} strokeWidth={1.8} />
      {children}
    </Button>
  );
}
function AvatarForUser({
  name,
  url,
}: {
  readonly name: string;
  readonly url: string | null;
}) {
  return (
    <Avatar className="user-avatar">
      {url === null ? null : <AvatarImage alt="" src={url} />}
      <AvatarFallback>{initials(name)}</AvatarFallback>
    </Avatar>
  );
}
function Field({
  label,
  name,
  type = "text",
  placeholder,
  required = false,
}: {
  readonly label: string;
  readonly name: string;
  readonly type?: string;
  readonly placeholder?: string;
  readonly required?: boolean;
}) {
  const id = `field-${name}`;
  return (
    <FieldRoot>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input
        id={id}
        name={name}
        type={type}
        placeholder={placeholder}
        required={required}
      />
    </FieldRoot>
  );
}
function Notice({ children }: { readonly children: React.ReactNode }) {
  return (
    <Alert role="status">
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  );
}
function EmptyState({
  className,
  title,
  detail,
}: {
  readonly className?: string;
  readonly title: string;
  readonly detail: string;
}) {
  return (
    <Empty className={className}>
      <EmptyHeader>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{detail}</EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

function sdkConfiguration(site: Site): string {
  return `import { createAppNotes } from "@plannotator/appnotes";

const appNotes = createAppNotes({
  mode: "connected",
  siteId: ${JSON.stringify(site.id)},
  apiOrigin: ${JSON.stringify(window.location.origin)},
});

await appNotes.mount();`;
}

function sdkSetupInstructions(site: Site): string {
  return `npm install @plannotator/appnotes

${sdkConfiguration(site)}`;
}
function LoadingRows() {
  return (
    <div className="grid gap-px border bg-border" aria-label="Loading">
      <Skeleton className="h-28 rounded-none" />
      <Skeleton className="h-28 rounded-none" />
      <Skeleton className="h-28 rounded-none" />
    </div>
  );
}
function SecretReveal({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}) {
  return <SecretPanel label={label} value={value} />;
}
function CredentialRecoveryQueue({
  onRecover,
  pending,
  recoveries,
}: {
  readonly onRecover: (recovery: CredentialRecovery) => void;
  readonly pending: PendingActions;
  readonly recoveries: readonly CredentialRecovery[];
}) {
  return (
    <section
      aria-labelledby="credential-recovery-title"
      className="credential-recovery-queue"
    >
      <header>
        <div>
          <h2 id="credential-recovery-title">Finish saving credentials</h2>
          <p>
            AppNotes kept a non-secret recovery receipt. Finish copying these
            values before their recovery windows close.
          </p>
        </div>
        <StatusBadge tone="primary">{recoveries.length}</StatusBadge>
      </header>
      <ul>
        {recoveries.map((recovery) => {
          const action = `credential:recover:${recovery.id}`;
          const recovering = pending.isPending(action);
          return (
            <li key={recovery.id}>
              <div>
                <strong>
                  {recovery.kind === "api_key"
                    ? recovery.name
                    : `Guest link for ${recovery.siteName}`}
                </strong>
                <span>
                  {recovery.kind === "api_key" ? "API key" : "Guest link"}
                  {" · Recovery available until "}
                  <time dateTime={recovery.recoverableUntil}>
                    {credentialRecoveryTime(recovery.recoverableUntil)}
                  </time>
                </span>
              </div>
              <Button
                disabled={recovering}
                onClick={() => onRecover(recovery)}
                size="sm"
                type="button"
              >
                {recovering ? "Recovering…" : "Finish saving"}
              </Button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function credentialRecoveriesForAccess(
  recoveries: readonly CredentialRecovery[],
  administrator: boolean,
  sites: readonly Site[],
): readonly CredentialRecovery[] {
  return recoveries.filter((recovery) => {
    if (recovery.kind === "api_key") return administrator;
    return sites.some((site) => site.id === recovery.siteId && site.enabled);
  });
}

function credentialRecoveryTime(recoverableUntil: string): string {
  return new Date(recoverableUntil).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

function credentialRetryAction(recovery: CredentialRecovery): string {
  return recovery.kind === "api_key"
    ? `api-key:create:${JSON.stringify([recovery.name, recovery.agentName ?? ""])}`
    : `guest-link:create:${recovery.siteId}`;
}

function credentialRecoveryCannotContinue(cause: unknown): boolean {
  return cause instanceof ApiError && [409, 410, 422].includes(cause.status);
}
function ConfirmButton({
  description,
  label,
  onConfirm,
  pending = false,
  title,
  variant = "destructive",
}: {
  readonly description: string;
  readonly label: string;
  readonly onConfirm: () => void;
  readonly pending?: boolean;
  readonly title: string;
  readonly variant?: "destructive" | "ghost";
}) {
  return (
    <AlertDialog>
      <AlertDialogTrigger
        disabled={pending}
        render={<Button size="xs" type="button" variant={variant} />}
      >
        {pending ? `${label}…` : label}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={pending}
            onClick={onConfirm}
            type="button"
            variant="destructive"
          >
            {pending ? `${label}…` : label}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
function GuestLinkItem({
  link,
  onRevoke,
  pending = false,
  siteEnabled = true,
}: {
  readonly link: GuestLink;
  readonly onRevoke?: () => void;
  readonly pending?: boolean;
  readonly siteEnabled?: boolean;
}) {
  const linkState = guestLinkState(link);
  const state = !siteEnabled && linkState === "Active" ? "Paused" : linkState;
  const lifecycle =
    state === "Paused"
      ? "Restore the Site to reactivate"
      : state === "Revoked"
        ? `Revoked ${relativeDate(link.revoked_at ?? link.created_at)}`
        : state === "Expired"
          ? `Expired ${calendarDate(link.expires_at ?? link.created_at)}`
          : link.expires_at === null
            ? "No expiration"
            : `Expires ${calendarDate(link.expires_at)}`;

  return (
    <li>
      <div>
        <StatusBadge
          tone={
            state === "Active"
              ? "primary"
              : state === "Revoked"
                ? "danger"
                : "neutral"
          }
        >
          {state}
        </StatusBadge>
        <span>Created {relativeDate(link.created_at)}</span>
      </div>
      <div>
        <span>{lifecycle}</span>
        {linkState === "Active" && onRevoke !== undefined && (
          <ConfirmButton
            description="This revokes the guest link and every active session created from it."
            label="Revoke"
            onConfirm={onRevoke}
            pending={pending}
            title="Revoke this guest link?"
            variant="ghost"
          />
        )}
      </div>
    </li>
  );
}
function LoadingScreen() {
  return (
    <div className="center-screen">
      <Brand />
      <p className="loading-label">Loading your workspace…</p>
    </div>
  );
}
function FatalScreen({
  message,
  retry,
}: {
  readonly message: string;
  readonly retry: () => void;
}) {
  return (
    <div className="center-screen">
      <Brand />
      <section className="gate-card">
        <h1>AppNotes could not load</h1>
        <p>{message}</p>
        <Button className="mt-6" type="button" onClick={retry}>
          Try again
        </Button>
      </section>
    </div>
  );
}
function SignInScreen() {
  const localLogout =
    new URLSearchParams(window.location.search).get("logout") === "local";
  return (
    <div className="signin-screen">
      <header>
        <Brand />
        <a
          className={buttonVariants({ variant: "outline" })}
          href="/auth/login"
        >
          Sign in
        </a>
      </header>
      <main>
        {localLogout && (
          <Alert className="mb-6 max-w-xl">
            <AlertTitle>Signed out of AppNotes</AlertTitle>
            <AlertDescription>
              Your identity provider may still be signed in. Sign out there too
              if you are using a shared device.
            </AlertDescription>
          </Alert>
        )}
        <p className="eyebrow">Feedback on the live interface</p>
        <h1>Keep the conversation attached to the product.</h1>
        <p>
          AppNotes gives product teams, clients, and agents one precise place to
          review what is actually on screen.
        </p>
        <a className={buttonVariants()} href="/auth/login?return_to=%2F">
          Sign in to AppNotes
        </a>
      </main>
    </div>
  );
}

function errorMessage(cause: unknown): string {
  return cause instanceof Error
    ? cause.message
    : "The request could not be completed.";
}

function requestCanceled(cause: unknown): boolean {
  return cause instanceof ApiError && cause.code === "request_cancelled";
}
function capitalize(value: string): string {
  return `${value.slice(0, 1).toUpperCase()}${value.slice(1)}`;
}
function initials(name: string): string {
  return name
    .split(/\s+/u)
    .slice(0, 2)
    .map((part) => part.slice(0, 1).toUpperCase())
    .join("");
}
function relativeDate(value: string): string {
  const elapsed = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(elapsed)) return "No activity";
  const minutes = Math.max(0, Math.floor(elapsed / 60_000));
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days < 30
    ? `${days}d ago`
    : new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "numeric",
      }).format(new Date(value));
}

function calendarDate(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "Unknown date";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(
    date,
  );
}

function guestLinkState(link: GuestLink): "Active" | "Expired" | "Revoked" {
  if (link.revoked_at !== null) return "Revoked";
  if (
    link.expires_at !== null &&
    new Date(link.expires_at).getTime() <= Date.now()
  ) {
    return "Expired";
  }
  return "Active";
}

function guestLinkStateRank(link: GuestLink): number {
  const state = guestLinkState(link);
  return state === "Active" ? 0 : state === "Expired" ? 1 : 2;
}

function guestLinkHistorySummary(links: readonly GuestLink[]): string {
  if (links.length === 0) return "No history";
  const revoked = links.filter(
    (link) => guestLinkState(link) === "Revoked",
  ).length;
  const expired = links.length - revoked;
  return [
    revoked > 0 ? `${revoked} revoked` : null,
    expired > 0 ? `${expired} expired` : null,
  ]
    .filter((value): value is string => value !== null)
    .join(" · ");
}

const rootElement = document.getElementById("root");
if (rootElement === null) {
  throw new Error("AppNotes could not find its root element.");
}

const root = createRoot(rootElement);
// Consume outside React so StrictMode cannot lose or exchange the link.
// Opening a replacement invitation in this tab can be a fragment-only visit.
let guestEntry: GuestEntry | null = null;
let guestVisit = 0;
function renderEntry() {
  guestEntry?.clear();
  guestEntry = GuestEntry.fromUrl(new URL(window.location.href));
  if (guestEntry !== null) {
    window.history.replaceState(null, "", window.location.pathname);
    guestVisit += 1;
  }
  root.render(
    <StrictMode>
      {guestEntry === null ? (
        <App />
      ) : (
        <GuestEntryScreen key={guestVisit} entry={guestEntry} />
      )}
    </StrictMode>,
  );
}
renderEntry();
if (guestEntry !== null) window.addEventListener("hashchange", renderEntry);
